import { axiosInstance as axios, handleAxiosErrors } from '../../_utils';
import config from '../../store/_config';
import { buildAddressParams, buildUpdateAddressParams } from '../../selectors/addressSelector';
import { convertUserDetailedInformationToAReadableFormat } from '../../selectors/converterSelector';
import {
    toggleLoading,
    setUserInfo,
    submitAccountHolderInformation,
} from '../userActions';
import { createAlert } from "../../dom/window";
import { getTranslation } from "../../lang/main";

/**
 * Request delivery addresses
 */
export const requestAddresses = () => {
    return (dispatch) => {
        dispatch(toggleLoading('addressList', true));

        return axiosRequestCallAfterDelete(0, 9999)
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.readOneMember
                );

                dispatch(setUserInfo(userInfo));

                dispatch(toggleLoading('addressList', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('addressList', false));
            });
    };
};

/**
 * Save business address
 *
 * @param formData
 * @param callback
 */
export const saveBusinessAddress = (formData, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        dispatch(requestAddresses());

        return axiosSaveBusinessAddress(formData)
            .then((response) => {
                // create alert
                createAlert({
                    message: getTranslation(
                        'ACCOUNT_HOLDER_INFORMATION_FORM.SUCCESS_MESSAGE'
                    ),
                }, 'success');

                dispatch(requestAddresses());

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};

/**
 * Create a new address
 *
 * @param formData
 * @param callback
 */
export const createAddress = (formData, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosCreateAddress(formData)
            .then((response) => {
                dispatch(requestAddresses());

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};
/**
 * Update address
 *
 * @param formData
 * @param callback
 */
export const updateAddress = (formData, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosUpdateAddress(formData)
            .then((response) => {
                dispatch(requestAddresses());

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};

/**
 * Set default billing address
 * It uses submitAccountHolderInformation() as main endpoint
 *
 * @param id
 * @param callback
 */
export const makeDefaultBillingAddress = (id, callback) => {
    return (dispatch) => {
        const newCallback = {
            success: (response) => {
                if (typeof callback.success != 'undefined') {
                    callback.success(response);
                }
                dispatch(requestDefaultBillingAddressID());
            },
            error: (response) => {
                if (typeof callback.error != 'undefined') {
                    callback.error(response);
                }
            },
            always: (response) => {
                if (typeof callback.always != 'undefined') {
                    callback.always(response);
                }
            },
        };

        return dispatch(
            submitAccountHolderInformation(
                { BillingAddressID: id },
                newCallback
            )
        );
    };
};

/**
 * Request default billing address ID
 * This is called after setting default billing address
 */
export const requestDefaultBillingAddressID = () => {
    return (dispatch) => {
        dispatch(toggleLoading('addressList', true));
        return axiosRequestDefaultBillingAddressID()
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.readOneMember
                );
                dispatch(setUserInfo(userInfo));

                dispatch(toggleLoading('addressList', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('addressList', false));
            });
    };
};
/**
 * Delete the selected address
 *
 * @param id
 * @param callback
 */
export const deleteAddress = (id, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosDeleteAddress(id)
            .then((response) => {
                dispatch(requestAddresses());

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};

export const axiosRequestCallAfterDelete = (offset=0,limit=5, search = '', orderOnBehalfOf = false , isAccountPage = false) => {
    let { url, query } = config.user.addressesAfterDeleteItem;
    if (orderOnBehalfOf === true) {
        query = query.replace(
            'readOneMember',
            `readOneMember(PurchasingUser: true)`
        );
    }
    
    return axios.get(url, {
        withCredentials: true,
        // variables:{limit:limit},
        // query: query,
        params: {
            query: query,
        },
    });
};

export const axiosRequestAddresses = (memberID = null) => {
    let { url, query } = config.user.addresses;

    if (memberID != null) {
        query = query.replace(
            'readOneMember',
            `readOneMember(ID: ${memberID})`
        );
    }

    return axios.get(url, {
        withCredentials: true,
        params: {
            query: query,
        },
    });
};
export const axiosRequestDefaultDeliveryAddressID = (memberID = null) => {
    let { url, query } = config.user.defaultDeliveryAddressID;

    if (memberID != null) {
        query = query.replace(
            'readOneMember',
            `readOneMember(ID: ${memberID})`
        );
    }

    return axios.get(url, {
        withCredentials: true,
        params: {
            query: query,
        },
    });
};
export const axiosRequestDefaultBillingAddressID = (memberID = null) => {
    let { url, query } = config.user.defaultBillingAddressID;

    if (memberID != null) {
        query = query.replace(
            'readOneMember',
            `readOneMember(ID: ${memberID})`
        );
    }

    return axios.get(url, {
        withCredentials: true,
        params: {
            query: query,
        },
    });
};
export const axiosSaveBusinessAddress = (formData) => {
    let { url, query } = config.user.saveBusinessAddress;

    let data = {
        id: query,
        variables: {
            Input: buildAddressParams(formData),
        },
    };

    return axios.post(url, data, {
        withCredentials: true,
    });
};
export const axiosCreateAddress = (formData, memberID = null) => {
    let { url, query } = config.user.createAddress;

    let data = {
        id: query,
        variables: {
            Input: buildAddressParams(formData),
        },
    };

    if (memberID != null) {
        data.variables.MemberID = memberID;
    }

    return axios.post(url, data, {
        withCredentials: true,
    });
};
export const axiosUpdateAddress = (formData, memberID = null) => {
    let { url, query } = config.user.updateAddress;

    let data = {
        id: query,
        variables: {
            Input: buildUpdateAddressParams(formData),
        },
    };

    if (memberID != null) {
        data.variables.MemberID = memberID;
    }

    return axios.post(url, data, {
        withCredentials: true,
    });
};
export const axiosDeleteAddress = (ID, memberID = null) => {
    let { url, query } = config.user.deleteAddress;

    let data = {
        id: query,
        variables: {
            ID: Number(ID),
        },
    };

    if (memberID != null) {
        data.variables.MemberID = memberID;
    }

    return axios.post(url, data, {
        withCredentials: true,
    });
};
export const axiosUpdateBillingAddress = (formData, memberID = null) => {
    let { url, query } = config.user.updateBillingAddress;

    let data = {
        id: query,
        variables: {
            Input: buildAddressParams(formData),
        },
    };

    if (memberID != null) {
        data.variables.MemberID = memberID;
    }

    return axios.post(url, data, {
        withCredentials: true,
    });
};

/**
 * Create a new Dispatch Location
 *
 * @param formData
 * @param callback
 */
export const saveDispatchLocation = (formData, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosSaveDispatchLocation(formData)
            .then((response) => {
                createAlert(
                    {
                        message: getTranslation(
                            'ACCOUNT_HOLDER_INFORMATION_FORM.SUCCESS_MESSAGE'
                        ),
                    },
                    'success'
                );
                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);

                /**
                 * Refreshing Dispatch Location List
                 */
                dispatch(requestDispatchLocations());
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};
export const axiosSaveDispatchLocation = (formData, dispatchLocationID = null) => {
    let { url, query } = config.user.saveDispatchLocation;

    let data = {
        id: query,
        variables: {
            Input: {
                Address: buildAddressParams(formData),
            }
        },
    };

    if (formData?.ID) {
        data.variables.Input.ID = formData?.ID;
    }

    console.log("DispatchLocation_FormDataToAxios", formData);

    return axios.post(url, data, {
        withCredentials: true,
    });
};
/**
 * Request Dispatch Locations
 */
export const requestDispatchLocations = (limit, search) => {
    return (dispatch) => {
        dispatch(toggleLoading('dispatchLocationList', true));
        return axiosDispatchLocations(limit, search)
            .then((response) => {
                const userInfo = convertUserDetailedInformationToAReadableFormat(
                    response.data.data.readOneMember
                );

                dispatch(setUserInfo(userInfo));
                dispatch(toggleLoading('dispatchLocationList', false));
            })
            .catch((error) => {
                handleAxiosErrors(error);
                dispatch(toggleLoading('dispatchLocationList', false));
            });
    };
};

export const axiosDispatchLocations = (limit=5,search ='' ,memberID = null) => {
    console.log('yahan aya dispatch');
    let { url, query } = config.user.dispatchLocations;

    // if (memberID != null) {
    //     query = query.replace(
    //         'readOneMember',
    //         `readOneMember(ID: ${memberID})`
    //     );
    // }

    // return axios.get(url, {
    //     withCredentials: true,
    //     params: {
    //         query: query,
    //     },
    // });

    let variables = {
        limit: limit,
        search: search,
    };
    if (memberID != null ) {
        variables.ID = memberID;
    }
    
    return axios.post(url, {
        withCredentials: true,
        variables: variables,
        query: query,
       
    });
};

/**
 * Delete the selected Dispatch Location
 *
 * @param id
 * @param callback
 */
export const deleteDispatchLocation = (id, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosDeleteDispatchLocation(id)
            .then((response) => {
                /**
                 * Refreshing Dispatch Location List
                 */
                dispatch(requestDispatchLocations());

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};
export const axiosDeleteDispatchLocation = (ID) => {
    let { url, query } = config.user.deleteDispatchLocation;

    let data = {
        id: query,
        variables: {
            ID: Number(ID),
        },
    };

    return axios.post(url, data, {
        withCredentials: true,
    });
};

/**
 * 
 * @param id 
 * @param callback 
 */
export const makeDefaultDispatchLocation = (id, callback) => {
    return (dispatch) => {
        const successCallback =
            typeof callback.success != 'undefined' ? callback.success : null;
        const errorCallback =
            typeof callback.error != 'undefined' ? callback.error : null;
        const alwaysCallback =
            typeof callback.always != 'undefined' ? callback.always : null;

        return axiosMakeDefaultDispatchLocation(id)
            .then((response) => {
                /**
                 * Refreshing Dispatch Location List
                 */
                dispatch(requestDispatchLocations());

                // Callbacks
                if (successCallback) successCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            })
            .catch((response) => {
                handleAxiosErrors(response);

                // Callbacks
                if (errorCallback) errorCallback(response);
                if (alwaysCallback) alwaysCallback(response);
            });
    };
};
export const axiosMakeDefaultDispatchLocation = (ID) => {
    let { url, query } = config.user.makeDefaultDispatchLocation;

    let data = {
        id: query,
        variables: {
            ID: Number(ID),
        },
    };

    return axios.post(url, data, {
        withCredentials: true,
    });
};
